// src/data/tickerDescriptions.ts
// Short descriptions for ticker chips and tooltips

const TICKER_DESC: Record<string, string> = {
  // ── Broad Market ──
  SPY: 'S&P 500 ETF',
  QQQ: 'Nasdaq 100 ETF',
  DIA: 'Dow Jones ETF',
  IWM: 'Russell 2000 ETF',

  // ── Sectors ──
  XLK: 'Tech Sector ETF',
  XLV: 'Healthcare Sector ETF',
  XLF: 'Financials Sector ETF',
  XLI: 'Industrials Sector ETF',
  XLU: 'Utilities Sector ETF',
  XLY: 'Consumer Discretionary ETF',
  XLE: 'Energy Sector ETF',
  XLC: 'Communication Services ETF',
  XLB: 'Materials Sector ETF',
  XLRE: 'Real Estate Sector ETF',
  XLP: 'Consumer Staples ETF',
  XOP: 'Oil & Gas Exploration ETF',
  OIH: 'Oil Services ETF',

  // ── Energy ──
  USO: 'US Oil Fund — tracks WTI crude oil futures',
  UCO: '2x Crude Oil ETF — leveraged oil exposure',
  XOM: 'ExxonMobil — largest US oil & gas supermajor, upstream + downstream + chemicals',
  CVX: 'Chevron — integrated oil & gas, major LNG producer',
  COP: 'ConocoPhillips — largest US pure-play E&P company',
  PXD: 'Pioneer Natural Resources — Permian Basin shale oil leader',
  OXY: 'Occidental Petroleum — oil & gas producer, Buffett-backed, carbon capture pioneer',
  SLB: "SLB (Schlumberger) — world's largest oilfield services company",
  HAL: 'Halliburton — oilfield services, hydraulic fracturing leader',
  BKR: 'Baker Hughes — oilfield services, LNG equipment manufacturer',
  MPC: 'Marathon Petroleum — largest US independent refiner',
  VLO: 'Valero Energy — major US refiner, renewable diesel producer',
  PSX: 'Phillips 66 — refining, midstream, and chemicals',
  DVN: 'Devon Energy — US shale oil & gas producer',

  // ── LNG & Natural Gas ──
  LNG: 'Cheniere Energy — largest US LNG exporter, Sabine Pass & Corpus Christi terminals',
  CQP: 'Cheniere Energy Partners — MLP for Sabine Pass LNG terminal',
  AR: 'Antero Resources — Appalachian natural gas & NGL producer',
  EQT: 'EQT Corporation — largest US natural gas producer',
  GLNG: 'Golar LNG — floating LNG liquefaction & shipping',
  UNG: 'US Natural Gas Fund — tracks Henry Hub nat gas futures',

  // ── Defense & Aerospace ──
  LMT: 'Lockheed Martin — F-35 fighter jets, missile defense systems, space',
  RTX: 'Raytheon Technologies — Patriot missiles, Pratt & Whitney engines, Collins avionics',
  NOC: 'Northrop Grumman — B-21 stealth bomber, space systems, autonomous aircraft',
  GD: 'General Dynamics — Abrams tanks, Gulfstream jets, nuclear submarines',
  BA: 'Boeing — commercial aircraft, defense & space systems, KC-46 tanker',
  HII: 'Huntington Ingalls — sole builder of US aircraft carriers & nuclear subs',
  LDOS: 'Leidos Holdings — defense IT, cybersecurity, intelligence services',
  CACI: 'CACI International — defense technology, intelligence & cyber solutions',
  ITA: 'Aerospace & Defense ETF',

  // ── Shipping & Tankers ──
  FRO: 'Frontline — one of the largest crude oil tanker fleets globally',
  STNG: 'Scorpio Tankers — product tanker fleet, refined petroleum transport',
  DHT: 'DHT Holdings — VLCC supertankers for crude oil shipping',
  INSW: 'International Seaways — crude and product tanker fleet',
  TEN: 'Tsakos Energy Navigation — diversified tanker fleet, LNG carriers',
  ZIM: 'ZIM Integrated Shipping — container shipping, Israel-based global carrier',
  MATX: 'Matson — Pacific shipping, Hawaii & Alaska trade routes',

  // ── Metals & Mining ──
  GLD: 'SPDR Gold ETF — tracks physical gold price',
  SLV: 'iShares Silver ETF — tracks physical silver price',
  GDX: 'Gold Miners ETF — basket of gold mining companies',
  GDXJ: 'Junior Gold Miners ETF — smaller gold mining explorers',
  IAU: 'iShares Gold Trust — tracks physical gold price',
  NEM: "Newmont — world's largest gold mining company",
  GOLD: 'Barrick Gold — major global gold & copper miner',
  GFI: 'Gold Fields — South African gold miner with global operations',
  AA: "Alcoa — one of the world's largest aluminum producers",
  CENX: 'Century Aluminum — US-based aluminum smelter',
  FCX: "Freeport-McMoRan — world's largest publicly traded copper producer",

  // ── Fertilizers & Agriculture ──
  MOS: "Mosaic — world's largest potash & phosphate producer",
  NTR: 'Nutrien — largest global crop nutrient company, retail distribution',
  CF: 'CF Industries — major nitrogen fertilizer producer, ammonia & urea',
  SMG: 'Scotts Miracle-Gro — lawn & garden products, hydroponics',
  ADM: 'Archer-Daniels-Midland — global grain processing & agricultural trading',
  BG: 'Bunge Global — agribusiness, grain trading & oilseed processing',

  // ── Petrochemicals ──
  LYB: 'LyondellBasell — one of the largest plastics, chemicals & refining companies',
  DOW: 'Dow Inc. — plastics, industrial chemicals, packaging materials',
  WLK: 'Westlake Chemical — PVC, polyethylene, building products',
  EMN: 'Eastman Chemical — specialty chemicals, advanced materials, fibers',
  HUN: 'Huntsman Corp — polyurethanes, specialty chemicals, textiles',
  CE: 'Celanese — engineered polymers, acetic acid, acetyl chain',

  // ── Airlines ──
  DAL: 'Delta Air Lines — largest US airline by revenue, major Atlanta hub',
  UAL: 'United Airlines — major US carrier, Chicago & Newark hubs',
  AAL: "American Airlines — world's largest airline by fleet, Dallas hub",
  LUV: 'Southwest Airlines — low-cost US carrier, point-to-point model',
  AL: 'Air Lease Corp — aircraft lessor, leases jets to airlines worldwide',
  ATSG: 'Air Transport Services — air cargo, Amazon Air fleet operator',
  JETS: 'Airlines ETF — basket of global airline stocks',

  // ── Automotive ──
  F: 'Ford Motor — F-150, Mustang, Bronco, electric vehicle transition',
  GM: 'General Motors — Chevy, GMC, Cadillac, Ultium EV platform',
  RIVN: 'Rivian — electric trucks & SUVs, Amazon delivery van partner',
  STLA: 'Stellantis — Jeep, Ram, Dodge, Chrysler, Fiat, Peugeot',
  TM: "Toyota — world's largest automaker, hybrid pioneer, solid-state battery R&D",

  // ── Semiconductors ──
  INTC: 'Intel — x86 CPUs, foundry services, data center chips',
  AMAT: 'Applied Materials — semiconductor manufacturing equipment, deposition & etch',
  LRCX: 'Lam Research — wafer fabrication equipment, plasma etch & deposition',
  ASML: 'ASML — sole maker of EUV lithography machines, critical to advanced chips',
  KLAC: 'KLA Corp — semiconductor process control & yield management equipment',
  SMH: 'Semiconductor ETF — VanEck, holds NVDA/TSM/ASML/AVGO',
  SOXX: 'Semiconductor ETF (iShares) — broad chip industry exposure',

  // ── Tech Giants ──
  AAPL: 'Apple — iPhone, Mac, services ecosystem, $3T+ market cap',
  MSFT: 'Microsoft — Windows, Azure cloud, Office 365, Copilot AI, GitHub',
  NVDA: 'NVIDIA — AI/ML GPU leader, data center accelerators, CUDA platform',
  GOOGL: 'Alphabet (Google) — search, YouTube, Google Cloud, Waymo, Android',
  META: 'Meta Platforms — Facebook, Instagram, WhatsApp, Reality Labs VR/AR',
  AMZN: 'Amazon — e-commerce, AWS cloud (largest globally), Prime, Alexa',
  TSLA: 'Tesla — electric vehicles, energy storage, Autopilot/FSD, Megapack',

  // ── Consumer & Retail ──
  NKE: "Nike — world's largest athletic footwear & apparel brand",
  WMT: "Walmart — world's largest retailer, grocery & e-commerce",
  COST: 'Costco — membership warehouse club, bulk retail',
  PVH: 'PVH Corp — Calvin Klein, Tommy Hilfiger global fashion',
  GPS: 'Gap Inc. — Gap, Old Navy, Banana Republic, Athleta',

  // ── Safe Haven & Macro ──
  TLT: '20+ Year Treasury ETF — long-duration US gov bonds, rate-sensitive',
  IEF: '7-10 Year Treasury ETF — intermediate-term US gov bonds',
  VXX: 'Volatility Index ETN — tracks VIX futures, spikes during selloffs',
  UUP: 'US Dollar Bullish ETF — tracks DXY dollar index',
  HYG: 'High Yield Bond ETF — corporate junk bonds, credit risk gauge',
  LQD: 'Investment Grade Bond ETF — high-quality corporate bonds',

  // ── Country/Region ETFs ──
  EWJ: 'Japan ETF — tracks MSCI Japan index',
  EWY: 'South Korea ETF — Samsung, SK Hynix heavy weight',
  EEM: 'Emerging Markets ETF — China, India, Brazil, Taiwan exposure',
  EFA: "EAFE (Developed Int'l) ETF — Europe, Australasia, Far East",
  FXI: 'China Large-Cap ETF — Alibaba, Tencent, ICBC heavy weight',
  KWEB: 'China Internet ETF — Chinese tech & e-commerce stocks',

  // ── Insurance ──
  AIG: 'AIG — global insurance, marine/war risk, commercial lines',
  MKL: 'Markel Group — specialty insurance, "baby Berkshire" conglomerate',
  RNR: 'RenaissanceRe — catastrophe reinsurance, risk analytics',
  ACGL: 'Arch Capital — specialty insurance & reinsurance, mortgage guarantee',

  // ── Thematic ETFs ──
  ARKK: 'ARK Innovation ETF — disruptive tech, genomics, fintech bets',
  XBI: 'Biotech ETF — equal-weight biotech, small/mid-cap heavy',
  XHB: 'Homebuilders ETF — residential construction & building products',
  XRT: 'Retail ETF — equal-weight retail sector, consumer spending gauge',
  KRE: 'Regional Banks ETF — US community & regional bank stocks',
  DBA: 'Agriculture ETF — corn, wheat, soybeans, sugar futures',

  // ── Other Mega-Caps ──
  JPM: 'JPMorgan Chase — largest US bank, investment banking, asset management',
  V: 'Visa — global payments network, processes $15T+ annually',
  MA: 'Mastercard — global payments network, 3B+ cards worldwide',
  UNH: 'UnitedHealth Group — largest US health insurer, Optum health services',
  HD: 'Home Depot — largest US home improvement retailer',
  PG: 'Procter & Gamble — consumer staples giant, Tide, Pampers, Gillette',
  JNJ: 'Johnson & Johnson — pharma, medical devices, consumer health',
  BAC: 'Bank of America — second-largest US bank, consumer & wealth management',
  CRM: 'Salesforce — cloud CRM leader, enterprise software, Slack',
  NFLX: 'Netflix — streaming entertainment, 260M+ subscribers globally',
  ABBV: 'AbbVie — pharma, Humira successor Skyrizi/Rinvoq, Botox (Allergan)',
  MRK: 'Merck — pharma giant, Keytruda cancer immunotherapy blockbuster',
  PEP: 'PepsiCo — beverages & Frito-Lay snacks, global consumer staple',
  KO: "Coca-Cola — world's largest beverage company, 200+ brands",
  AVGO: 'Broadcom — semiconductor & infrastructure software, networking chips, VMware',
  TMO: 'Thermo Fisher — life sciences instruments, lab equipment, biopharma services',
  ACN: 'Accenture — global IT consulting, digital transformation, managed services',
  LLY: "Eli Lilly — pharma, Mounjaro/Zepbound weight loss drugs, Alzheimer's treatments",
  ORCL: 'Oracle — enterprise databases, cloud infrastructure, SaaS applications',
  AMD: 'AMD — CPUs, GPUs, data center accelerators, EPYC server chips',
  QCOM: 'Qualcomm — mobile Snapdragon chips, 5G modems, IoT & automotive',
  ADBE: 'Adobe — Creative Cloud, Photoshop, PDF, digital marketing analytics',
};

export default TICKER_DESC;
